import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, History } from 'lucide-react';
import { matchingAPI } from '../../lib/api';

function getErrorMessage(err) {
  return err?.response?.data?.detail || err?.message || 'Request failed';
}

export function MatchEventBackfillPanel() {
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);

  const runPreview = async () => {
    setPreviewLoading(true);
    setApplyResult(null);
    try {
      const res = await matchingAPI.previewMatchEventBackfill();
      setPreview(res.data);
    } catch (err) {
      setPreview({ error: getErrorMessage(err) });
    } finally {
      setPreviewLoading(false);
    }
  };

  const runApply = async () => {
    const total = (preview?.single_confirm_count || 0) + (preview?.group_confirm_count || 0);
    if (!window.confirm(
      `Backfill ${total} missing confirm event(s) (${preview?.single_confirm_count || 0} single, ` +
      `${preview?.group_confirm_count || 0} group)? Each is written reconstructed=True with an ` +
      'approximated timestamp -- never confused with a live-witnessed confirm.'
    )) return;
    setApplyLoading(true);
    try {
      const res = await matchingAPI.applyMatchEventBackfill();
      setApplyResult(res.data);
    } catch (err) {
      setApplyResult({ error: getErrorMessage(err) });
    } finally {
      setApplyLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Match-Event Backfill</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Writes a reconstructed confirm/group_confirm event (services/match_events.py) for every
          currently-confirmed match that predates that write path -- otherwise the BR OS Paid
          Re-entry tallies and any other event-driven read undercount them. Preview counts what
          Apply would write; nothing is inserted until Apply.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={runPreview} disabled={previewLoading} data-testid="match-event-backfill-preview">
            {previewLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Preview
          </Button>
          {preview && !preview.error && ((preview.single_confirm_count || 0) + (preview.group_confirm_count || 0) > 0) && (
            <Button
              variant="default"
              size="sm"
              onClick={runApply}
              disabled={applyLoading || !!applyResult}
              data-testid="match-event-backfill-apply"
            >
              {applyLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Apply ({(preview.single_confirm_count || 0) + (preview.group_confirm_count || 0)})
            </Button>
          )}
        </div>

        {preview && (
          <>
            {preview.error ? (
              <p className="text-xs text-red-600">{preview.error}</p>
            ) : (
              <div className="space-y-2" data-testid="match-event-backfill-preview-result">
                <div className="flex flex-wrap gap-3 text-xs">
                  <span>Single-invoice: <strong>{preview.single_confirm_count}</strong></span>
                  <span>Group: <strong>{preview.group_confirm_count}</strong></span>
                </div>
                <p className="text-xs text-muted-foreground">{preview.message}</p>
              </div>
            )}
          </>
        )}

        {applyResult && (
          <div className={`p-3 rounded text-xs border ${applyResult.error ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`} data-testid="match-event-backfill-apply-result">
            {applyResult.error ? (
              <p className="text-red-600">{applyResult.error}</p>
            ) : (
              <div className="space-y-1">
                <p><strong>Written:</strong> {applyResult.single_written} single, {applyResult.group_written} group</p>
                <p className="text-muted-foreground">{applyResult.message}</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
